import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import { TResultSheet } from "@/typings/Props";
import { useTranslation } from "react-i18next";
import { theme } from "@/styles/theme";
import { media } from "@/styles/media";
import ResultSheetModal from "@/components/products/detail/ResultSheetModal";

export default function ResultSheet({
  ...props
}: {
  datas: TResultSheet[];
  isProduct?: boolean;
  $contentHeight: string;
  $imgWidth?: string;
  $textWidth?: string;
  $marginTop?: string;
  $AreaMarginTop?: string;
  $titleColor?: string;
  className?: string;
}) {
  const [tabId, setTabId] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const router = useRouter();
  const { t } = useTranslation();

  function handleTabBtn(tab: number) {
    setTabId(tab);
  }
  useEffect(() => {
    setTabId(1);
  }, [router.asPath]);

  return (
    <ResultSheetWrapper className={props.className}>
      <Ul>
        {props.datas.map((data: TResultSheet) => {
          const isActive = data.id === tabId;
          return (
            <Li key={data.id}>
              <Button
                onClick={() => handleTabBtn(data.id)}
                style={{
                  color: isActive ? `${theme.colors.primary}` : `${theme.colors.text.grayDark}`,
                  textDecoration: isActive ? `underline ${theme.colors.primary} 3px` : "none",
                  textUnderlineOffset: "26px",
                }}
              >
                {t(data.category)}
              </Button>
            </Li>
          );
        })}
      </Ul>
      {props.datas.map((data: TResultSheet) => {
        if (tabId == data.id) {
          if (data.desc_1 === undefined) {
            return (
              <>
                <Content $height={props.$contentHeight} $marginTop={props.$marginTop} />
                <ContentWrapper key={data.id} $height={props.$contentHeight} className="content-wrapper">
                  <Area $marginTop={props.$AreaMarginTop} className="left-area">
                    <Img
                      src={data.image}
                      $width={props.$imgWidth}
                      $isPointer={props.isProduct}
                      $marginTop={props.$marginTop}
                      onClick={() => setIsModalOpen(true)}
                    />
                  </Area>
                  <Area className="right-area">
                    <AnalysisTitle $color={props.$titleColor} $width={props.$textWidth}>
                      {t(data.title as string)}
                    </AnalysisTitle>
                    <AnalysisDesc $width={props.$textWidth}>{t(data.desc as string)}</AnalysisDesc>
                  </Area>
                  {props.isProduct && (
                    <ResultSheetModal
                      isOpen={isModalOpen}
                      onClose={() => setIsModalOpen(false)}
                      path={data.image as string}
                    />
                  )}
                </ContentWrapper>
              </>
            );
          } else {
            // inbody technology
            return (
              <ContentWrapper id="descWrapper" key={data.id}>
                <DescItem $backgroundColor={theme.colors.background.primary}>
                  <ComparisonItemTitle $color={theme.colors.text.white}>
                    {t("menu.learn.comparison.category.title-1")}
                  </ComparisonItemTitle>
                  <ComparisonItemDesc $color={theme.colors.text.white}>
                    {t(`menu.learn.comparison.category-${tabId}.desc-1`)}
                  </ComparisonItemDesc>
                </DescItem>
                <DescItem $backgroundColor={theme.colors.background.default}>
                  <ComparisonItemTitle>{t("menu.learn.comparison.category.title-2")}</ComparisonItemTitle>
                  <ComparisonItemDesc $color={theme.colors.text.grayDark}>
                    {t(`menu.learn.comparison.category-${tabId}.desc-2`)}
                  </ComparisonItemDesc>
                </DescItem>
              </ContentWrapper>
            );
          }
        }
      })}
    </ResultSheetWrapper>
  );
}

const ResultSheetWrapper = styled.div`
  margin-bottom: 250px;
  flex-grow: 1;

  ${media.tablet`
    margin-bottom: initial;
    `}
`;

const Ul = styled.ul`
  display: flex;
  justify-content: center;
  list-style: none;
  border-bottom: 1px solid #e1e1e1;

  ${media.tablet`
    flex-wrap: wrap;
  `}
`;

const Li = styled.li`
  padding: 25px;
  color: grey;
`;

const Button = styled.button`
  background-color: rgba(1, 1, 1, 0);
  border: none;
  color: ${theme.colors.text.grayDark};
  transition: 0.2s ease-out;
  cursor: pointer;

  &:hover {
    transition: 0.2s ease-out;
    color: black;
  }
`;

const ContentWrapper = styled.div<{ $height?: string }>`
  width: 100%;
  height: ${({ $height }) => $height};
  padding: 30px 40px;
  display: flex;
  justify-content: center;
  gap: 20px;

  ${media.tablet`
    flex-direction: column;
    height: 100%;
    `}
`;

const Content = styled.div<{ $height?: string; $resHeight?: string; $marginTop?: string }>`
  position: absolute;
  left: 0;
  width: 100%;
  height: ${({ $height }) => $height};
  margin-top: ${({ $marginTop }) => $marginTop};
  background-color: ${theme.colors.background.white};
  z-index: 0;

  ${media.tablet`
    background-color: transparent;
  `}
`;

const DescItem = styled.div<{ $backgroundColor: string }>`
  width: 42%;
  padding: 36px 21px;
  padding-bottom: 60px;
  margin: 25px 0 70px 0;
  border-radius: 10px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};

  ${media.tablet`
    width: 100%;
    margin: 0;
  `}
`;

const Area = styled.div<{ $marginTop?: string }>`
  margin: auto;
  margin-top: ${({ $marginTop }) => $marginTop};
  flex: 1;
  z-index: 1000;

  ${media.tablet`
    width: 100%;
  `}
`;

const Img = styled.img<{ $width?: string; $isPointer?: boolean; $marginTop?: string }>`
  max-width: 670px;
  width: ${({ $width }) => $width};
  height: 100%;
  display: block;
  margin: auto;
  margin-top: ${({ $marginTop }) => $marginTop};
  border-radius: 10px;
  object-fit: contain;
  cursor: ${({ $isPointer }) => ($isPointer ? "pointer" : "default")};

  ${media.tablet`
    width: 100%;
    height: 100%;
  `}
`;

const AnalysisTitle = styled.p<{ $width?: string; $color?: string }>`
  width: ${({ $width }) => $width};
  margin-top: 50px;
  margin-bottom: 10px;
  font-size: 2rem;
  white-space: pre-wrap;
  font-weight: ${theme.fontWeights.bold};
  color: ${({ $color }) => ($color ? $color : `${theme.colors.text.grayDark}`)};

  ${media.tablet`
      margin-top: 0;
  `}
`;

const AnalysisDesc = styled.p<{ $width?: string }>`
  width: ${({ $width }) => $width};
  line-height: 170%;
  white-space: pre-wrap;
  color: ${theme.colors.text.grayLight};

  ${media.tablet`
      width: 100%;
  `}
`;

const ComparisonItemTitle = styled.p<{ $color?: string }>`
  margin-bottom: 15px;
  font-size: 2rem;
  font-weight: ${theme.fontWeights.bold};
  color: ${({ $color }) => $color};
`;

const ComparisonItemDesc = styled.p<{ $color: string }>`
  line-height: 150%;
  color: ${({ $color }) => $color};
`;
